import React, { useMemo, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from 'recharts';
import { Spinner } from 'components';
import DateRangePicker from './Daterangepicker';

import { ReactComponent as IconFollowers }    from '../../../assets/icons/Dropdown/Group_2085663789.svg';
import { ReactComponent as IconImpressions }  from '../../../assets/icons/Dropdown/Group_2085663919.svg';
import { ReactComponent as IconReach }        from '../../../assets/icons/Dropdown/Group_2085663920.svg';
import { ReactComponent as IconClicks }       from '../../../assets/icons/Dropdown/Group.svg';
import { ReactComponent as IconProfileVisit } from '../../../assets/icons/Dropdown/Group_2085663922.svg';
import { ReactComponent as IconReferral }     from '../../../assets/icons/Dropdown/Group-1.svg';
import { ReactComponent as IconFavourites }   from '../../../assets/icons/Dropdown/Group-2.svg';
import { ReactComponent as IconLastDays }     from '../../../assets/icons/DatePicker/Group_2085663933.svg';
import { ReactComponent as IconSelectMonth }  from '../../../assets/icons/DatePicker/Group_2085663934.svg';
import { ReactComponent as IconDateRange }    from '../../../assets/icons/DatePicker/Group_2085663935.svg';
import { ReactComponent as IconCalendar }     from '../../../assets/icons/DatePicker/Group_2085663936.svg';

/* ── Generate last N months ───────────────────────────────────── */
const generateRecentMonths = (count = 12) => {
  const months = [];
  const now = new Date();
  for (let i = 0; i < count; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({
      key:       `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      label:     d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }),
      year:      d.getFullYear(),
      month:     d.getMonth() + 1,
      startDate: new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10),
      endDate:   new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10),
    });
  }
  return months;
};

const RECENT_MONTHS = generateRecentMonths(12);

/* ── Dropdown options ─────────────────────────────────────────── */
const REDEMPTION_OPTIONS = [
  { key: 'followers',        label: 'Followers',         icon: IconFollowers    },
  { key: 'impressions',      label: 'Impressions',       icon: IconImpressions  },
  { key: 'reach',            label: 'Reach',             icon: IconReach        },
  { key: 'clicks',           label: 'Clicks',            sublabel: '(Detail Views)', icon: IconClicks },
  { key: 'profile_visits',   label: 'Profile Visits',    icon: IconProfileVisit },
  { key: 'referral_signups', label: 'Referral Sign-ups', icon: IconReferral     },
  { key: 'favourites',       label: 'Favourites',        icon: IconFavourites   },
];

const TIME_OPTIONS = [
  { key: 'last_30_days', label: 'Last 30 Days', icon: IconLastDays    },
  { key: 'select_month', label: 'Select Month', icon: IconSelectMonth },
  { key: 'date_range',   label: 'Date Range',   icon: IconDateRange   },
];

/* ── Custom tooltip ───────────────────────────────────────────── */
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{ background: '#3B0A1F', borderRadius: '14px', padding: '8px 14px', fontSize: '12px', color: 'white', boxShadow: '0 4px 12px rgba(0,0,0,0.2)', minWidth: '150px' }}>
        <div style={{ fontWeight: '600', marginBottom: '6px', color: '#fff' }}>{label}</div>
        {payload.map((entry, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginBottom: '2px' }}>
            <span style={{ color: entry.color, fontWeight: '500' }}>{entry.name}</span>
            <span style={{ fontWeight: '700' }}>{entry.value?.toLocaleString()}</span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const ChevronDown = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M3 5l4 4 4-4" stroke="#888" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

/* ═══════════════════════════════════════════════════════════════ */

const PointsDollarsChart = ({ data, fetching, fetched, onFilterChange }) => {
  const [redemptionOpen, setRedemptionOpen] = useState(false);
  const [timeOpen, setTimeOpen]             = useState(false);
  const [monthOpen, setMonthOpen]           = useState(false);
  const [selectedRedemption, setSelectedRedemption] = useState(REDEMPTION_OPTIONS[0]);
  const [selectedTime, setSelectedTime]     = useState(TIME_OPTIONS[0]);
  const [selectedMonth, setSelectedMonth]   = useState(null);
  const [appliedRange, setAppliedRange]     = useState(null);
  const [showPicker, setShowPicker]         = useState(false);

  const redemptionRef = useRef(null);
  const timeRef       = useRef(null);

  useEffect(() => {
    const handler = e => {
      if (redemptionRef.current && !redemptionRef.current.contains(e.target)) setRedemptionOpen(false);
      if (timeRef.current && !timeRef.current.contains(e.target)) {
        setTimeOpen(false);
        setMonthOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const dispatchFilter = ({ redemption = selectedRedemption, time = selectedTime, range = appliedRange, month = selectedMonth }) => {
    let period;
    if (time.key === 'date_range' && range) {
      period = { key: 'date_range', startDate: range.startDate, endDate: range.endDate };
    } else if (time.key === 'select_month' && month) {
      period = { key: 'select_month', startDate: month.startDate, endDate: month.endDate, year: month.year, month: month.month };
    } else {
      period = { key: time.key };
    }
    onFilterChange?.({ type: redemption.key, period });
  };

  const handleTimeOptionClick = (opt) => {
    setSelectedTime(opt);
    if (opt.key === 'last_30_days') {
      setTimeOpen(false);
      setMonthOpen(false);
      setSelectedMonth(null);
      setAppliedRange(null);
      dispatchFilter({ time: opt, range: null, month: null });
    } else if (opt.key === 'select_month') {
      setMonthOpen(o => !o);
    } else if (opt.key === 'date_range') {
      setTimeOpen(false);
      setMonthOpen(false);
      setShowPicker(true);
    }
  };

  const handleMonthSelect = (month) => {
    setSelectedMonth(month);
    setAppliedRange(null);
    setMonthOpen(false);
    setTimeOpen(false);
    dispatchFilter({ time: TIME_OPTIONS.find(o => o.key === 'select_month'), range: null, month });
  };

  const combinedData = useMemo(() => {
    const pointsRaw  = data.amount_points_received_chart  || [];
    const dollarsRaw = data.amount_dollars_received_chart || [];
    if (!pointsRaw.length && !dollarsRaw.length) return [];
    const maxLen = Math.max(pointsRaw.length, dollarsRaw.length);
    return Array.from({ length: maxLen }, (_, i) => {
      const p = pointsRaw[i];
      const d = dollarsRaw[i];
      return {
        name:    p?.name   || p?.label || p?.month || d?.name || d?.label || d?.month || `${i + 1}`,
        points:  p?.points ?? p?.points_back ?? p?.value ?? p?.amount ?? 0,
        dollars: d?.dollars ?? d?.amount_off  ?? d?.value ?? d?.amount ?? 0,
      };
    });
  }, [data]);

  const total = (data.points_from_customers || 0) + (data.dollars_from_customers || 0);

  const yAxisConfig = useMemo(() => {
    if (!combinedData.length) return { domain: [0, 100], ticks: [0, 25, 50, 75, 100] };
    const maxVal    = Math.max(...combinedData.map(d => Math.max(d.points, d.dollars)));
    const magnitude = Math.pow(10, Math.floor(Math.log10(maxVal || 1)));
    const niceMax   = Math.ceil((maxVal * 1.2) / magnitude) * magnitude;
    const step      = niceMax / 5;
    const ticks     = Array.from({ length: 6 }, (_, i) => Math.round(i * step));
    return { domain: [0, niceMax], ticks };
  }, [combinedData]);

  const timeButtonLabel = useMemo(() => {
    if (selectedTime.key === 'select_month' && selectedMonth) return selectedMonth.label;
    if (selectedTime.key === 'date_range'   && appliedRange) {
      const fmt = d => new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
      return `${fmt(appliedRange.startDate)} – ${fmt(appliedRange.endDate)}`;
    }
    return selectedTime.label;
  }, [selectedTime, selectedMonth, appliedRange]);

  return (
    <>
      {/* ─── ROOT: width:100% so the parent wrapper (flex:1 1 50%) controls the size ─── */}
      <div style={{
        background: '#F2EEEE',
        borderRadius: '24px',
        padding: '20px 16px',
        width: '100%',       /* fills whatever the parent gives it  */
        minWidth: 0,         /* allows shrinking below content size  */
        boxSizing: 'border-box',
        fontFamily: "'Segoe UI', sans-serif",
        position: 'relative',
        zIndex: 10,
      }}>

        {/* ── Top controls ── */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', position: 'relative', zIndex: 10 }}>

          {/* Metric dropdown */}
          <div ref={redemptionRef} style={{ position: 'relative' }}>
            <button
              onClick={() => { setRedemptionOpen(o => !o); setTimeOpen(false); setMonthOpen(false); }}
              style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 16px', background: 'white', border: '1.5px solid #e8e0e0', borderRadius: '24px', cursor: 'pointer', minWidth: '170px', justifyContent: 'space-between' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <selectedRedemption.icon width={20} height={20} />
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#1a1a1a' }}>{selectedRedemption.label}</span>
              </div>
              <ChevronDown />
            </button>

            {redemptionOpen && (
              <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, background: 'white', borderRadius: '16px', boxShadow: '0 8px 28px rgba(0,0,0,0.12)', minWidth: '220px', zIndex: 50, padding: '6px 0', border: '1px solid #f0e8e8' }}>
                {REDEMPTION_OPTIONS.map((opt, i) => (
                  <button
                    key={opt.key}
                    onClick={() => { setSelectedRedemption(opt); setRedemptionOpen(false); dispatchFilter({ redemption: opt }); }}
                    style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%', padding: '11px 18px', background: selectedRedemption.key === opt.key ? '#fdf5f5' : 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', borderBottom: i < REDEMPTION_OPTIONS.length - 1 ? '1px solid #f5f0f0' : 'none' }}
                  >
                    <opt.icon width={20} height={20} />
                    <span style={{ fontSize: '14px', fontWeight: '500', color: '#1a1a1a' }}>
                      {opt.label}
                      {opt.sublabel && <span style={{ fontSize: '12px', color: '#aaa', marginLeft: '4px' }}>{opt.sublabel}</span>}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Time dropdown */}
          <div ref={timeRef} style={{ position: 'relative' }}>
            <button
              onClick={() => { setTimeOpen(o => !o); setRedemptionOpen(false); if (timeOpen) setMonthOpen(false); }}
              style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 16px', background: 'white', border: '1.5px solid #e8e0e0', borderRadius: '24px', cursor: 'pointer' }}
            >
              <IconCalendar width={18} height={18} />
              <span style={{ fontSize: '14px', fontWeight: '500', color: '#1a1a1a' }}>{timeButtonLabel}</span>
              <ChevronDown />
            </button>

            {timeOpen && (
              <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, background: 'white', borderRadius: '14px', boxShadow: '0 8px 28px rgba(0,0,0,0.12)', minWidth: '200px', zIndex: 50, padding: '6px 0', border: '1px solid #f0e8e8' }}>
                {TIME_OPTIONS.map((opt, i) => (
                  <button
                    key={opt.key}
                    onClick={() => handleTimeOptionClick(opt)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      gap: '10px', width: '100%', padding: '10px 16px',
                      background: selectedTime.key === opt.key ? '#fdf0f0' : 'transparent',
                      border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: '14px',
                      fontWeight: selectedTime.key === opt.key ? '600' : '400',
                      color: selectedTime.key === opt.key ? '#D4001D' : '#1a1a1a',
                      borderBottom: i < TIME_OPTIONS.length - 1 ? '1px solid #f5f0f0' : 'none',
                    }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <opt.icon width={16} height={16} />
                      {opt.label}
                    </span>
                    {opt.key === 'select_month' && (
                      <svg width="12" height="12" viewBox="0 0 14 14" fill="none"
                        style={{ transform: monthOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s', flexShrink: 0 }}>
                        <path d="M3 5l4 4 4-4" stroke="#bbb" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </button>
                ))}

                {monthOpen && (
                  <div style={{ borderTop: '1px solid #f5f0f0', maxHeight: '220px', overflowY: 'auto', padding: '4px 0' }}>
                    {RECENT_MONTHS.map(month => (
                      <button
                        key={month.key}
                        onClick={() => handleMonthSelect(month)}
                        style={{
                          display: 'flex', alignItems: 'center', width: '100%',
                          padding: '9px 20px', border: 'none', cursor: 'pointer', textAlign: 'left',
                          fontSize: '13px',
                          background: selectedMonth?.key === month.key ? '#fdf0f0' : 'transparent',
                          fontWeight: selectedMonth?.key === month.key ? '600' : '400',
                          color: selectedMonth?.key === month.key ? '#D4001D' : '#444',
                        }}
                      >
                        {month.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ── Legend ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: '#D4001D' }} />
            <span style={{ fontSize: '12px', color: '#555' }}>Points</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: '#F5D97A' }} />
            <span style={{ fontSize: '12px', color: '#555' }}>Dollars</span>
          </div>
        </div>

        {/* ── Stat pills ── */}
        <div style={{ display: 'flex', gap: '4px', marginBottom: '16px' }}>
          {[
            { label: 'Points',  value: data.points_from_customers  || 0 },
            { label: 'Dollars', value: data.dollars_from_customers || 0 },
            { label: 'Total',   value: total },
          ].map(({ label, value }) => (
            <div key={label} style={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '4px',
              background: 'white',
              borderRadius: '10px',
              padding: '6px 8px',
              border: '1px solid #ede8e8',
              overflow: 'hidden',
            }}>
              <span style={{
                fontSize: 'clamp(9px, 0.7vw, 11px)',
                color: '#888',
                fontWeight: '400',
                whiteSpace: 'nowrap',
                flexShrink: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>{label}</span>
              <span style={{
                fontSize: 'clamp(10px, 0.8vw, 14px)',
                fontWeight: '700',
                color: '#1a1a1a',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}>{value}</span>
            </div>
          ))}
        </div>

        {/* ── Chart ── */}
        {fetched && combinedData.length > 0 ? (
          <div style={{ background: 'white', borderRadius: '16px', padding: '16px 8px 8px 0' }}>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={combinedData} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#aaa', fontWeight: '500' }} axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={40} />
                <YAxis tick={{ fontSize: 11, fill: '#aaa' }} axisLine={false} tickLine={false} tickFormatter={v => v >= 1000 ? `${parseFloat((v / 1000).toFixed(1))}k` : v} domain={yAxisConfig.domain} ticks={yAxisConfig.ticks} width={36} />
                <Tooltip content={<CustomTooltip />} cursor={false} />
                <Line type="monotoneX" dataKey="points"  name="Points"  stroke="#D4001D" strokeWidth={2.5} dot={false} activeDot={{ r: 6, fill: '#3B0A1F', strokeWidth: 0 }} />
                <Line type="monotoneX" dataKey="dollars" name="Dollars" stroke="#F5C97A" strokeWidth={2.5} dot={false} activeDot={{ r: 6, fill: '#F5C97A', strokeWidth: 0 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div style={{ height: '160px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#aaa', fontSize: '13px', gap: '8px' }}>
            <Spinner show={fetching} />
            {!fetching && 'No points/dollars data available'}
          </div>
        )}
      </div>

      {/* ── Date Range Picker modal ── */}
      {showPicker && (
        <DateRangePicker
          onClose={() => {
            setShowPicker(false);
            if (!appliedRange) setSelectedTime(TIME_OPTIONS[0]);
          }}
          onApply={({ startDate, endDate }) => {
            const range = { startDate, endDate };
            setAppliedRange(range);
            setSelectedMonth(null);
            setShowPicker(false);
            dispatchFilter({ time: TIME_OPTIONS.find(o => o.key === 'date_range'), range, month: null });
          }}
        />
      )}
    </>
  );
};

PointsDollarsChart.propTypes = {
  data:           PropTypes.object.isRequired,
  fetching:       PropTypes.bool,
  fetched:        PropTypes.bool,
  onFilterChange: PropTypes.func,
};

export default PointsDollarsChart;